/**
 * components/ScrollStrip.tsx — a horizontal strip that scrolls rather than wraps, and says so at both ends.
 *
 * Two defects of one strip, found by the conformity audit of 7 August 2026, fixed here rather than in the
 * single screen that has a strip today — because the second of them is a component's worth of behaviour
 * and the next strip this application grows must not have to rediscover it.
 *
 * **It wrapped.** `.admin-subnav` was `flex-wrap: wrap` at every width, with no media query anywhere
 * changing it. `03-components.md` §8's mobile rule and `02-patterns.md` §2.4's restatement of it are the
 * same sentence: the tabs "scroll horizontally with the selected one scrolled into view; they MUST NOT
 * wrap to a second row". Five sections at the 44px coarse-pointer floor wrap to two and then three rows
 * on a phone, and each extra row moves the page head further from the top bar — the one distance §2.1
 * fixes as a constant of the app, varying by screen inside a single console.
 *
 * **Once it scrolls, §8.1 binds immediately**, and §8.1 (design-system v7.0.0, never audited here) is why
 * this is a component rather than three lines of stylesheet. Its failure is silent at both ends. The
 * reader sees a row that looks complete — a strip is cut between whole pills rather than mid-cell, and it
 * has no scrollbar to give the cut away — and never learns the sections beyond the edge exist; they are
 * not hard to reach, they are undiscoverable, and nobody reports them missing. The author sees a strip
 * that behaves perfectly on the desktop it was built on, where it wraps. `04-data.md` §1.4 states plainly
 * why a TABLE may answer this with a fade or a scrollbar and a strip may not: a table keeps a real
 * scrollbar and a sticky first column and is cut mid-cell, so its rows visibly continue. A strip has none
 * of that.
 *
 * What §8.1 asks for, and where each half of it lives:
 *
 *   - **A direction, not a soft edge.** The gradient in the stylesheet is legibility; the chevron is the
 *     clause. "A fade says *there is more of this*; it does not say the row is a thing you can move, and
 *     a reader who has never moved one does not learn it from a gradient."
 *   - **Every width it overflows at, and every pointer type.** Nothing here sits behind a media query and
 *     nothing consults `useIsMobile`: what is measured is the element, so a long section list on a
 *     desktop at 200% zoom is answered by the same code as a phone. Nothing about a mouse makes a cut-off
 *     row easier to notice than a thumb does.
 *   - **No keyboard stop**, which is `aria-hidden` with `tabIndex={-1}`. This is the one place in the
 *     whole document where hiding an operable control from assistive technology is permitted, and it is
 *     permitted only because the second half holds: the strip stays fully scrollable without the
 *     indicator, by keyboard, by swipe and by wheel. Focus already moves through the strip's own links
 *     and the browser scrolls the focused one into view — which is also why the viewport itself takes no
 *     `tabindex`. A stop in front of every strip in the app buys a keyboard user nothing.
 *   - **A press leaves part of the previous view on screen** — `ADVANCE_FRACTION`, below.
 *   - **A strip that fits draws nothing**, so the buttons are unmounted rather than hidden. "An indicator
 *     that is always there is not an indicator."
 *
 * **What this component deliberately does not own: semantics.** The row is passed in as `children` and
 * arrives complete — `AdminConsole` hands it a `<nav>` of anchors carrying `aria-current`. §2.4 refuses
 * the tablist model to a strip whose destinations are addresses, and a scroll container is not the place
 * to relitigate that. The only thing read out of the children here is the single `[aria-current]` node,
 * and only to scroll it into view.
 *
 * ⚠️ **The measurement is the whole component, and it is the half a test misses by accident.** jsdom
 * computes no layout at all: `scrollWidth` and `clientWidth` are both 0 there, so a component that
 * decides anything by measuring decides it identically in every test unless the test supplies the
 * numbers itself — which `ScrollStrip.test.tsx` does, explicitly, with `Object.defineProperty`.
 * `ResizeObserver`, `scrollTo` and `scrollIntoView` are all absent from jsdom too, and each is
 * feature-detected below for the same reason `ui.tsx` feature-detects `matchMedia`: unguarded, the
 * component with the most behaviour to prove becomes the one nothing can prove.
 */
import { useCallback, useEffect, useRef, useState, type ReactNode } from "react";

/**
 * The pixel of slack the v7.0.0 migration note asks for.
 *
 * A strip laid out at 328.4px reports a `scrollWidth` of 329 against a `clientWidth` of 328, so a strict
 * `>` comparison hands a strip that *fits* a permanent trailing chevron — and an indicator that is always
 * there is not an indicator. One pixel is the whole of the rounding error and no more: two would begin
 * hiding a genuine overflow.
 */
const EDGE_SLACK_PX = 1;

/**
 * How far one press moves the row, as a fraction of the viewport's width.
 *
 * §8.1: a press "MUST leave part of the previous view on screen. A press that advances by exactly one
 * strip-width hands the reader a wholly new set of names with nothing to locate themselves by." Four
 * fifths leaves a fifth of the row behind, which at every width this strip is drawn at is at least one
 * whole tab — something to locate oneself by, which is the point. That it is strictly less than 1 is the
 * property worth testing; the exact fraction is not.
 */
const ADVANCE_FRACTION = 0.8;

/** Which edges have content behind them. Both false is a strip that fits, and draws nothing. */
interface Edges {
  readonly start: boolean;
  readonly end: boolean;
}

const NEITHER: Edges = { start: false, end: false };

/**
 * Compare `scrollWidth` against `clientWidth` and `scrollLeft`, allowing the slack above.
 *
 * Written as a free function, and not as a closure over the ref, so the arithmetic can be reasoned about
 * on its own: three numbers in, two booleans out, and no React in the middle of it.
 */
function measureEdges(viewport: HTMLElement): Edges {
  const overflow = viewport.scrollWidth - viewport.clientWidth;
  if (overflow <= EDGE_SLACK_PX) return NEITHER;
  const left = viewport.scrollLeft;
  return { start: left > EDGE_SLACK_PX, end: left < overflow - EDGE_SLACK_PX };
}

/**
 * The one motion preference this file has to read in JavaScript rather than leave to the stylesheet.
 *
 * `styles.css`'s `prefers-reduced-motion` block sets `scroll-behavior: auto !important`, which governs
 * the CSS property — and a `behavior: "smooth"` passed to `scrollTo` overrides the CSS property, so the
 * declaration would be silently walked past. Feature-detected like `matchMedia` everywhere else here:
 * absent the API, no preference has been expressed and the animation stands.
 */
function prefersReducedMotion(): boolean {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") return false;
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

/**
 * Move the row to an absolute offset, clamped to what there is.
 *
 * `scrollTo` is feature-detected because jsdom implements neither it nor `scrollBy`; assigning
 * `scrollLeft` is the older path and works everywhere. The clamp is here rather than at the call site so
 * that a press at either end is a no-op rather than a negative offset the browser silently corrects —
 * and so the two callers cannot disagree about what "as far as it goes" means.
 */
function scrollViewportTo(viewport: HTMLElement, left: number): void {
  const furthest = Math.max(0, viewport.scrollWidth - viewport.clientWidth);
  const target = Math.max(0, Math.min(left, furthest));
  if (typeof viewport.scrollTo === "function") {
    viewport.scrollTo({ left: target, behavior: prefersReducedMotion() ? "auto" : "smooth" });
    return;
  }
  viewport.scrollLeft = target;
}

/**
 * Bring the item carrying `aria-current` into view. §8's mobile rule, the half that is not about wrapping.
 *
 * Instant, never smooth, and deliberately: this is a correction of where the row starts, not a response
 * to a press. An animation the reader did not ask for, on arrival at a page, is motion for its own sake.
 *
 * `block: "nearest"` keeps it from scrolling the document vertically to reach a strip that is already on
 * screen. The arithmetic fallback exists for jsdom, where `scrollIntoView` is not implemented at all;
 * `offsetLeft` is measured from the offset parent — `.scroll-strip`, which the stylesheet positions — so
 * it is the item's distance along the row and does not move with the current scroll offset.
 */
function bringCurrentIntoView(viewport: HTMLElement): void {
  const current = viewport.querySelector<HTMLElement>("[aria-current]");
  if (current === null) return;
  if (typeof current.scrollIntoView === "function") {
    current.scrollIntoView({ block: "nearest", inline: "nearest", behavior: "auto" });
    return;
  }
  scrollViewportTo(viewport, current.offsetLeft - (viewport.clientWidth - current.offsetWidth) / 2);
}

export interface ScrollStripProps {
  /**
   * Identifies the item currently carrying `aria-current`. A change re-runs the scroll-into-view.
   *
   * Deliberately opaque — this component does not know what a section is. It is a change signal, and the
   * caller's own notion of "which one is current" is the only thing that can produce it honestly.
   */
  readonly currentKey: string | null;
  /** The row. Arrives complete, with its own element, label and semantics. */
  readonly children: ReactNode;
}

export function ScrollStrip(props: ScrollStripProps): JSX.Element {
  const { currentKey, children } = props;
  const viewportRef = useRef<HTMLDivElement | null>(null);
  const [edges, setEdges] = useState<Edges>(NEITHER);

  /**
   * Re-read the element and store the answer, but only when it has changed.
   *
   * The guard is not a micro-optimisation: this runs on every scroll event, and returning the previous
   * object keeps React from re-rendering the strip sixty times a second while a thumb is on it.
   */
  const remeasure = useCallback((): void => {
    const viewport = viewportRef.current;
    if (viewport === null) return;
    const next = measureEdges(viewport);
    setEdges((previous) => (previous.start === next.start && previous.end === next.end ? previous : next));
  }, []);

  useEffect(() => {
    const viewport = viewportRef.current;
    if (viewport === null) return;
    remeasure();
    viewport.addEventListener("scroll", remeasure, { passive: true });

    const observer = typeof ResizeObserver === "function" ? new ResizeObserver(() => remeasure()) : null;
    if (observer === null) {
      // No `ResizeObserver`: the window's own resize is the fallback, and it is a weaker one. It misses
      // every case where the strip changes size without the window doing so — the rail collapsing, a
      // section appearing as a grant lands, a font finishing loading — which is exactly why the observer
      // is preferred wherever it exists.
      window.addEventListener("resize", remeasure);
    } else {
      observer.observe(viewport);
      // The ROW as well as the viewport, because the case this strip actually has is the other one: the
      // viewport keeps its width and the row grows, as the sections dilate with the caller's grants.
      const row = viewport.firstElementChild;
      if (row !== null) observer.observe(row);
    }

    return () => {
      viewport.removeEventListener("scroll", remeasure);
      if (observer === null) window.removeEventListener("resize", remeasure);
      else observer.disconnect();
    };
  }, [remeasure]);

  useEffect(() => {
    const viewport = viewportRef.current;
    if (viewport === null) return;
    bringCurrentIntoView(viewport);
    // A programmatic scroll fires its `scroll` event asynchronously in a browser and not at all where
    // the offset was assigned directly, so the edges are re-read here rather than left to the listener.
    remeasure();
  }, [currentKey, remeasure]);

  const advance = useCallback(
    (direction: -1 | 1): void => {
      const viewport = viewportRef.current;
      if (viewport === null) return;
      scrollViewportTo(viewport, viewport.scrollLeft + direction * viewport.clientWidth * ADVANCE_FRACTION);
      remeasure();
    },
    [remeasure],
  );

  return (
    <div className="scroll-strip">
      <div className="scroll-strip__viewport" ref={viewportRef}>
        {children}
      </div>
      {edges.start ? (
        <button
          type="button"
          className="scroll-strip__edge scroll-strip__edge--start"
          aria-hidden="true"
          tabIndex={-1}
          onClick={() => advance(-1)}
        >
          {/* U+2039/U+203A rather than the geometric arrowheads `ui.tsx` uses elsewhere. U+25C0 and
              U+25B6 carry an emoji presentation on iOS and Android and turn up as coloured play buttons
              in the middle of a section strip; these two have no emoji form to fall into. */}
          ‹
        </button>
      ) : null}
      {edges.end ? (
        <button
          type="button"
          className="scroll-strip__edge scroll-strip__edge--end"
          aria-hidden="true"
          tabIndex={-1}
          onClick={() => advance(1)}
        >
          ›
        </button>
      ) : null}
    </div>
  );
}
